import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  Grid,
  Button,
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { fetchEmailConfiguration, sendTestEmail, updateEmailConfiguration } from '../../../services/api';
import useLoading from '../../../hooks/useLoading';

const SECURITY_OPTIONS = [
  { value: 'STARTTLS', label: 'STARTTLS', defaultPort: 587 },
  { value: 'SSL', label: 'SSL/TLS', defaultPort: 465 },
  { value: 'NONE', label: 'None (unencrypted)', defaultPort: 25 },
];

const initialConfig = {
  enabled: false,
  host: '',
  port: 587,
  security: 'STARTTLS',
  requiresAuth: true,
  username: '',
  password: '',
  fromAddress: '',
  fromName: '',
  replyTo: '',
  toAddresses: '',
  ccAddresses: '',
  // Set from the API: a password is stored on the server. The password itself is never sent back.
  hasPassword: false,
};

// UI state <-> the snake_case JSON the API stores; recipient lists are arrays there, comma text here.
const fromApi = (data) => ({
  ...initialConfig,
  enabled: !!data?.enabled,
  host: data?.host || '',
  port: data?.port ?? initialConfig.port,
  security: data?.security || initialConfig.security,
  requiresAuth: data?.requires_auth ?? initialConfig.requiresAuth,
  username: data?.username || '',
  fromAddress: data?.from_address || '',
  fromName: data?.from_name || '',
  replyTo: data?.reply_to || '',
  toAddresses: (data?.to_addresses || []).join(', '),
  ccAddresses: (data?.cc_addresses || []).join(', '),
  hasPassword: !!data?.has_password,
});

const toApi = (config) => ({
  enabled: config.enabled,
  host: config.host.trim(),
  port: config.port === '' ? null : Number(config.port),
  security: config.security,
  requires_auth: config.requiresAuth,
  username: config.username.trim(),
  // Blank keeps the stored password.
  password: config.password,
  from_address: config.fromAddress.trim(),
  from_name: config.fromName.trim(),
  reply_to: config.replyTo.trim(),
  to_addresses: parseEmailList(config.toAddresses),
  cc_addresses: parseEmailList(config.ccAddresses),
});

const cardHeaderSx = {
  backgroundColor: '#f5f5f5',
  '& .MuiCardHeader-title': { fontSize: '1.1rem', fontWeight: 600 },
};

// Deliberately simple: one @, something either side, a dot in the domain, no spaces.
const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

const isEmail = (value) => EMAIL_PATTERN.test(value);

/** Splits a comma-separated list, trimming blanks and repeated addresses (case-insensitive). */
export const parseEmailList = (text) => {
  const seen = new Set();
  return (text || '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => {
      if (!s) return false;
      const key = s.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

function SecretField({ label, value, onChange, disabled, stored }) {
  const [show, setShow] = useState(false);
  return (
    <TextField
      label={label}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      disabled={disabled}
      required={!stored}
      placeholder={stored ? '••••••••' : undefined}
      InputLabelProps={stored ? { shrink: true } : undefined}
      helperText={stored ? 'Saved. Leave blank to keep it.' : undefined}
      fullWidth
      size="small"
      autoComplete="new-password"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShow((s) => !s)} edge="end" disabled={disabled}>
              {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

/** Comma-separated address box; flags any address that is not valid. */
function EmailListField({ label, value, onChange, disabled, required, placeholder }) {
  const invalid = useMemo(() => parseEmailList(value).filter((a) => !isEmail(a)), [value]);

  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      error={invalid.length > 0}
      helperText={invalid.length
        ? `Not a valid email address: ${invalid.join(', ')}`
        : 'Separate multiple addresses with commas'}
      multiline
      minRows={2}
      fullWidth
      size="small"
    />
  );
}

function validate(config) {
  if (!config.enabled) return [];
  const errors = [];
  if (!config.host.trim()) errors.push('SMTP host is required');
  const port = Number(config.port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) errors.push('Port must be between 1 and 65535');
  if (config.requiresAuth) {
    if (!config.username.trim()) errors.push('Username is required');
    if (!config.password && !config.hasPassword) errors.push('Password is required');
  }
  if (!config.fromAddress.trim()) errors.push('From address is required');
  else if (!isEmail(config.fromAddress.trim())) errors.push('From address is not a valid email address');
  if (config.replyTo.trim() && !isEmail(config.replyTo.trim())) errors.push('Reply-to is not a valid email address');

  const to = parseEmailList(config.toAddresses);
  if (!to.length) errors.push('At least one recipient is required');
  const badTo = to.filter((a) => !isEmail(a));
  if (badTo.length) errors.push(`Invalid recipient: ${badTo.join(', ')}`);
  const badCc = parseEmailList(config.ccAddresses).filter((a) => !isEmail(a));
  if (badCc.length) errors.push(`Invalid CC address: ${badCc.join(', ')}`);
  return errors;
}

export default function EmailConfig() {
  const [config, setConfig] = useState(initialConfig);
  // 'loading' | 'ready' | 'error'. Save is only possible once the stored config is in the form:
  // saving the empty initial form would overwrite the stored configuration.
  const [loadState, setLoadState] = useState('loading');
  // The settings as last loaded or saved, so the test can say when it would not use what is on screen.
  const [savedConfig, setSavedConfig] = useState(null);
  const [testAddress, setTestAddress] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const { withLoading } = useLoading();
  // The test-email tool is for whoever sets up SMTP, so it only appears with ?dev=true.
  const { search } = useLocation();
  const showAdvanced = useMemo(() => new URLSearchParams(search).get('dev') === 'true', [search]);
  const update = (patch) => setConfig((prev) => ({ ...prev, ...patch }));
  const disabled = !config.enabled;

  const load = useCallback(async () => {
    setLoadState('loading');
    await withLoading(async () => {
      try {
        const res = await fetchEmailConfiguration();
        const loaded = fromApi(res?.response_data);
        setConfig(loaded);
        setSavedConfig(loaded);
        setTestAddress((current) => current || parseEmailList(loaded.toAddresses)[0] || '');
        setLoadState('ready');
      } catch (error) {
        console.error('Error fetching email configuration:', error);
        setLoadState('error');
      }
    });
  }, [withLoading]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSecurityChange = (security) => {
    const { defaultPort } = SECURITY_OPTIONS.find((o) => o.value === security);
    update({ security, port: defaultPort });
  };

  const handleSave = async () => {
    if (loadState !== 'ready') return;
    const errors = validate(config);
    if (errors.length) {
      toast.error(errors.join('\n'));
      return;
    }
    await withLoading(async () => {
      try {
        const res = await updateEmailConfiguration(toApi(config));
        const saved = fromApi(res?.response_data);
        setConfig(saved);
        setSavedConfig(saved);
        toast.success(res?.user_message || 'Email configuration saved');
      } catch (error) {
        // 4xx/5xx messages are already shown by the API client interceptor.
        console.error('Error saving email configuration:', error);
      }
    });
  };

  // The test uses the saved settings; unsaved edits (other than a typed password) would not be tested.
  const hasUnsavedChanges = !!config.password || (!!savedConfig &&
    JSON.stringify({ ...config, password: '' }) !== JSON.stringify({ ...savedConfig, password: '' }));
  const canTest = loadState === 'ready' && !!savedConfig?.host && !!savedConfig?.fromAddress;

  const handleSendTest = async () => {
    const address = testAddress.trim();
    if (!isEmail(address)) {
      toast.error('Enter a valid email address to send the test to');
      return;
    }
    setSendingTest(true);
    try {
      const res = await sendTestEmail(address);
      toast.success(res?.user_message || `Test email sent to ${address}`);
    } catch (error) {
      // The server's reason (wrong password, refused connection, ...) is shown by the API client interceptor.
      console.error('Error sending test email:', error);
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Box p={3} sx={{ height: '100%', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
          Email Configuration
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={load} disabled={loadState === 'loading'}>Discard changes</Button>
          <Button variant="contained" onClick={handleSave} disabled={loadState !== 'ready'}>Save</Button>
        </Box>
      </Box>

      {loadState === 'error' && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          The saved email configuration could not be loaded, so it cannot be changed right now.
        </Alert>
      )}

      {loadState === 'ready' && (
      <Grid container spacing={3} direction="column">
        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader
              title="SMTP Server"
              subheader="The mail server used to send notification emails"
              action={
                <FormControlLabel
                  control={<Switch checked={config.enabled} onChange={(e) => update({ enabled: e.target.checked })} />}
                  label="Enabled"
                />
              }
              sx={cardHeaderSx}
            />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="SMTP host"
                    placeholder="smtp.office365.com"
                    value={config.host}
                    onChange={(e) => update({ host: e.target.value })}
                    disabled={disabled}
                    required
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    label="Security"
                    value={config.security}
                    onChange={(e) => handleSecurityChange(e.target.value)}
                    disabled={disabled}
                    fullWidth
                    size="small"
                  >
                    {SECURITY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Port"
                    type="number"
                    value={config.port}
                    onChange={(e) => update({ port: e.target.value })}
                    disabled={disabled}
                    required
                    fullWidth
                    size="small"
                    inputProps={{ min: 1, max: 65535 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.requiresAuth}
                        onChange={(e) => update({ requiresAuth: e.target.checked })}
                        disabled={disabled}
                      />
                    }
                    label="Server requires authentication"
                  />
                </Grid>
                {config.requiresAuth && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Username"
                        value={config.username}
                        onChange={(e) => update({ username: e.target.value })}
                        disabled={disabled}
                        required
                        fullWidth
                        size="small"
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SecretField
                        label="Password"
                        value={config.password}
                        onChange={(e) => update({ password: e.target.value })}
                        disabled={disabled}
                        stored={config.hasPassword}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader title="Sender" subheader="How emails appear to the people who receive them" sx={cardHeaderSx} />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="From address"
                    placeholder="noreply@example.com"
                    value={config.fromAddress}
                    onChange={(e) => update({ fromAddress: e.target.value })}
                    disabled={disabled}
                    required
                    error={!!config.fromAddress.trim() && !isEmail(config.fromAddress.trim())}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="From name"
                    placeholder="PRIME Provider Payer Connect"
                    value={config.fromName}
                    onChange={(e) => update({ fromName: e.target.value })}
                    disabled={disabled}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Reply-to address"
                    placeholder="Optional"
                    value={config.replyTo}
                    onChange={(e) => update({ replyTo: e.target.value })}
                    disabled={disabled}
                    error={!!config.replyTo.trim() && !isEmail(config.replyTo.trim())}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader title="Recipients" subheader="Who receives the notification emails" sx={cardHeaderSx} />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <EmailListField
                    label="To"
                    placeholder="ops@example.com, team.lead@example.com"
                    value={config.toAddresses}
                    onChange={(toAddresses) => update({ toAddresses })}
                    disabled={disabled}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <EmailListField
                    label="CC"
                    placeholder="Optional"
                    value={config.ccAddresses}
                    onChange={(ccAddresses) => update({ ccAddresses })}
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {showAdvanced && (
        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader title="Send a test email" subheader="Check the saved SMTP settings by sending one message" sx={cardHeaderSx} />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  label="Send test to"
                  value={testAddress}
                  onChange={(e) => setTestAddress(e.target.value)}
                  size="small"
                  sx={{ flex: '1 1 280px' }}
                  error={!!testAddress.trim() && !isEmail(testAddress.trim())}
                  helperText={hasUnsavedChanges
                    ? 'You have unsaved changes. The test uses the saved settings, so save first.'
                    : 'Uses the saved settings. The email includes a sample download link.'}
                />
                <Button
                  variant="outlined"
                  onClick={handleSendTest}
                  disabled={!canTest || sendingTest || hasUnsavedChanges}
                  sx={{ height: 40 }}
                >
                  {sendingTest ? 'Sending…' : 'Send test email'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>
      )}
    </Box>
  );
}
